import api, { STORAGE_KEYS, clearTokens } from './api';

/**
 * Admin Authentication Service
 * Handles all authentication-related API calls
 */
const authService = {
    /**
     * Login admin
     * @param {string} email - Admin email
     * @param {string} password - Admin password
     * @returns {Promise<Object>} - Login response with tokens and user data
     */
    login: async (email, password, rememberMe = true) => {
        try {
            const response = await api.post('/admin/login', { email, password }, false);

            if (response.success && response.data) {
                // حفظ التوكن: localStorage إذا "تذكرني" مفعّل، sessionStorage إذا لا
                const storage = rememberMe ? localStorage : sessionStorage;
                storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.data.accessToken);
                storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.data.refreshToken);
                storage.setItem(STORAGE_KEYS.ADMIN_DATA, JSON.stringify(response.data.user));
                
                return {
                    success: true,
                    data: response.data.user,
                    message: response.message
                };
            }
            
            return {
                success: false,
                error: response.message || 'البريد الإلكتروني أو كلمة المرور غير صحيحة'
            };
        } catch (error) {
            // عرض رسالة الخطأ من الـ API
            return {
                success: false,
                error: error.message || 'البريد الإلكتروني أو كلمة المرور غير صحيحة'
            };
        }
    },

    /**
     * Logout admin
     * @returns {Promise<Object>} - Logout response
     */
    logout: async () => {
        try {
            const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
            
            if (refreshToken) {
                await api.post('/admin/logout', { refreshToken });
            }
        } catch (error) {
            console.error('Logout API error:', error);
        } finally {
            // Always clear local storage
            clearTokens();
        }
        
        return { success: true };
    },

    /**
     * Logout from all devices
     * @returns {Promise<Object>} - Logout response
     */
    logoutAll: async () => {
        try {
            await api.post('/admin/logout-all', {});
        } catch (error) {
            console.error('Logout all API error:', error);
        } finally {
            clearTokens();
        }
        
        return { success: true };
    },

    /**
     * Refresh access token
     * @returns {Promise<Object>} - New tokens
     */
    refreshToken: async () => {
        try {
            const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
            
            if (!refreshToken) {
                return { success: false, error: 'No refresh token' };
            }
            
            const response = await api.post('/admin/refresh-token', { refreshToken }, false);
            
            if (response.success && response.data) {
                // حفظ في نفس مكان التخزين الأصلي
                const storage = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ? localStorage : sessionStorage;
                storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.data.accessToken);
                storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.data.refreshToken);

                return { success: true, data: response.data };
            }
            
            return { success: false, error: response.message };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },

    /**
     * Get stored admin data
     * @returns {Object|null} - Admin data or null
     */
    getStoredAdmin: () => {
        try {
            const adminData = localStorage.getItem(STORAGE_KEYS.ADMIN_DATA) || sessionStorage.getItem(STORAGE_KEYS.ADMIN_DATA);
            return adminData ? JSON.parse(adminData) : null;
        } catch {
            return null;
        }
    },

    /**
     * Check if admin is authenticated
     * @returns {boolean} - True if authenticated
     */
    isAuthenticated: () => {
        const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        const adminData = localStorage.getItem(STORAGE_KEYS.ADMIN_DATA) || sessionStorage.getItem(STORAGE_KEYS.ADMIN_DATA);
        return !!(token && adminData);
    },

    /**
     * Update stored admin data
     * @param {Object} updates - Data to update
     */
    updateStoredAdmin: (updates) => {
        try {
            const current = authService.getStoredAdmin();
            const updated = { ...current, ...updates };
            const storage = localStorage.getItem(STORAGE_KEYS.ADMIN_DATA) ? localStorage : sessionStorage;
            storage.setItem(STORAGE_KEYS.ADMIN_DATA, JSON.stringify(updated));
            return updated;
        } catch {
            return null;
        }
    }
};

export default authService;
